import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Check, ArrowLeft, ArrowRight } from "lucide-react";

const MAKES = ["Maruti Suzuki", "Hyundai", "Honda", "Tata", "Mahindra", "Kia", "Toyota", "Volkswagen", "BMW", "Mercedes-Benz", "Audi", "Skoda", "Renault", "Nissan", "Ford", "Other"];
const MODELS = {
  "Maruti Suzuki": ["Swift", "Baleno", "WagonR", "Alto", "Dzire", "Brezza", "Ertiga", "Grand Vitara", "Other"],
  "Hyundai": ["i20", "Creta", "Venue", "Verna", "Alcazar", "i10", "Other"],
  "Honda": ["City", "Amaze", "Jazz", "WR-V", "Elevate", "Other"],
  "Tata": ["Nexon", "Punch", "Harrier", "Safari", "Tiago", "Altroz", "Other"],
  "Mahindra": ["XUV700", "XUV300", "Scorpio", "Thar", "Bolero", "Other"],
  "Kia": ["Seltos", "Sonet", "Carens", "Carnival", "Other"],
  "Toyota": ["Innova Crysta", "Fortuner", "Glanza", "Urban Cruiser", "Hilux", "Other"],
  "Volkswagen": ["Taigun", "Virtus", "Polo", "Vento", "Other"],
  "BMW": ["3 Series", "5 Series", "X1", "X3", "X5", "Other"],
};
const FUELS = ["Petrol", "Diesel", "CNG", "Electric", "Hybrid"];
const TRANS = ["Manual", "Automatic", "AMT", "CVT", "DCT"];
const RTOS = ["DL - Delhi", "HR - Haryana", "UP - Uttar Pradesh", "RJ - Rajasthan", "PB - Punjab", "Other"];
const YEARS = Array.from({ length: 20 }, (_, i) => new Date().getFullYear() - i);
const KM_RANGES = ["0 – 10K", "10K – 20K", "20K – 40K", "40K – 60K", "60K – 80K", "80K – 1L", "1L+"];

export default function SellCarPage() {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ make: "", model: "", fuel: "", transmission: "", rto: "", year: "", km_range: "", registration_number: "", phone: "", email: "", name: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const total = 10;
  const next = () => setStep((s) => Math.min(total, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));
  const set = (k, v) => setF({ ...f, [k]: v });

  const canNext = () => {
    const map = { 1: f.make, 2: f.model, 3: f.fuel, 4: f.transmission, 5: f.rto, 6: f.year, 7: f.km_range, 8: f.registration_number, 9: f.phone && f.name };
    return !!map[step];
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/leads/sell-car", {
        name: f.name, phone: f.phone, email: f.email,
        make: f.make, model: f.model, year: f.year ? parseInt(f.year) : null,
        registration_number: f.registration_number, location: f.rto,
        message: `Fuel: ${f.fuel} | Trans: ${f.transmission} | KM: ${f.km_range}${f.message ? ` | ${f.message}` : ""}`,
      });
      setDone(true);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="cs-container py-20 max-w-xl text-center">
      <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-emerald-50 text-emerald-600 mx-auto"><Check size={30} /></div>
      <h1 className="mt-5 font-display font-extrabold text-3xl text-gray-900">Thanks, {f.name}!</h1>
      <p className="mt-3 text-gray-600">Our Carz Swag team will review your car details and contact you with an approximate valuation.</p>
    </div>
  );

  return (
    <div className="cs-container py-10 max-w-2xl" data-testid="sell-car-page">
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-gray-900">Sell Your Car to Carz Swag</h1>
      <p className="mt-2 text-gray-500 text-sm">Quick guided flow — takes less than a minute.</p>
      <div className="mt-6 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#940C2C] transition-all" style={{ width: `${(step / total) * 100}%` }} />
      </div>
      <div className="mt-2 text-xs text-gray-500">Step {step} of {total}</div>

      <div className="mt-6 border border-gray-200 rounded-2xl p-6 bg-white min-h-[280px]">
        {step === 1 && <Grid title="Select your car's make">{MAKES.map((m) => <Pill key={m} active={f.make === m} onClick={() => { set("make", m); setF((p) => ({ ...p, make: m, model: "" })); next(); }}>{m}</Pill>)}</Grid>}
        {step === 2 && <Grid title={`Which ${f.make} model?`}>{(MODELS[f.make] || ["Other"]).map((m) => <Pill key={m} active={f.model === m} onClick={() => { set("model", m); next(); }}>{m}</Pill>)}</Grid>}
        {step === 3 && <Grid title="Fuel type">{FUELS.map((x) => <Pill key={x} active={f.fuel === x} onClick={() => { set("fuel", x); next(); }}>{x}</Pill>)}</Grid>}
        {step === 4 && <Grid title="Transmission">{TRANS.map((x) => <Pill key={x} active={f.transmission === x} onClick={() => { set("transmission", x); next(); }}>{x}</Pill>)}</Grid>}
        {step === 5 && <Grid title="RTO location">{RTOS.map((x) => <Pill key={x} active={f.rto === x} onClick={() => { set("rto", x); next(); }}>{x}</Pill>)}</Grid>}
        {step === 6 && <Grid title="Registration year" cols={5}>{YEARS.map((y) => <Pill key={y} active={f.year === String(y)} onClick={() => { set("year", String(y)); next(); }}>{y}</Pill>)}</Grid>}
        {step === 7 && <Grid title="KM driven">{KM_RANGES.map((x) => <Pill key={x} active={f.km_range === x} onClick={() => { set("km_range", x); next(); }}>{x}</Pill>)}</Grid>}
        {step === 8 && (
          <div>
            <h3 className="font-display font-bold text-lg">Registration number</h3>
            <input autoFocus value={f.registration_number} onChange={(e) => set("registration_number", e.target.value.toUpperCase())} placeholder="DL1C AB1234" className="mt-4 w-full rounded-md border border-gray-200 px-4 py-3 text-base uppercase tracking-wider" data-testid="sell-regno" />
          </div>
        )}
        {step === 9 && (
          <div className="space-y-3">
            <h3 className="font-display font-bold text-lg">Your contact details</h3>
            <input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" className="w-full rounded-md border border-gray-200 px-4 py-3 text-sm" data-testid="sell-name" />
            <input value={f.phone} onChange={(e) => set("phone", e.target.value)} type="tel" placeholder="Phone number" className="w-full rounded-md border border-gray-200 px-4 py-3 text-sm" data-testid="sell-phone" />
            <input value={f.email} onChange={(e) => set("email", e.target.value)} type="email" placeholder="Email (optional)" className="w-full rounded-md border border-gray-200 px-4 py-3 text-sm" />
          </div>
        )}
        {step === 10 && (
          <div>
            <h3 className="font-display font-bold text-lg">Anything else we should know?</h3>
            <textarea rows={4} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="Service history, modifications, condition…" className="mt-3 w-full rounded-md border border-gray-200 px-4 py-3 text-sm" />
            <div className="mt-5 bg-gray-50 rounded-lg p-4 text-xs text-gray-600 space-y-1">
              <div><strong>Car:</strong> {f.year} {f.make} {f.model}</div>
              <div><strong>Details:</strong> {f.fuel} • {f.transmission} • {f.km_range}</div>
              <div><strong>RTO:</strong> {f.rto} • <strong>Reg:</strong> {f.registration_number}</div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between">
        <button onClick={prev} disabled={step === 1} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 disabled:opacity-30"><ArrowLeft size={14} /> Back</button>
        {step < total ? (
          <button onClick={next} disabled={!canNext()} className="btn-primary disabled:opacity-50" data-testid="sell-next">Continue <ArrowRight size={14} /></button>
        ) : (
          <button onClick={submit} disabled={submitting} className="btn-primary" data-testid="sell-submit">{submitting ? "Submitting…" : "Get Your Car Evaluated"}</button>
        )}
      </div>
    </div>
  );
}

const Grid = ({ title, cols = 3, children }) => (
  <div>
    <h3 className="font-display font-bold text-lg mb-4">{title}</h3>
    <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>{children}</div>
  </div>
);
const Pill = ({ active, onClick, children }) => (
  <button onClick={onClick} className={`rounded-lg border px-3 py-3 text-sm font-semibold transition-all ${active ? "border-[#940C2C] bg-[#F6E6EB] text-[#940C2C]" : "border-gray-200 text-gray-800 hover:border-gray-900"}`}>{children}</button>
);
